import React from 'react';
import { Box, Paper, Typography, Stack } from '@mui/material';
import { PhoneCallResponse, WebCallResponse } from 'retell-sdk/resources/call';

interface RetellCallTranscriptionSectionProps {
  call: PhoneCallResponse | WebCallResponse;
  open: boolean;
}

interface TranscriptEntry {
  role: 'agent' | 'user';
  content: string;
  timestamp?: number;
}

type ToolCallEntry =
  | PhoneCallResponse.Utterance
  | PhoneCallResponse.ToolCallInvocationUtterance
  | PhoneCallResponse.ToolCallResultUtterance
  | PhoneCallResponse.DtmfUtterance;

const RetellCallTranscriptionSection: React.FC<
  RetellCallTranscriptionSectionProps
> = ({ call }) => {
  const callDetails = call;
  const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const parseToolCallTranscript = (
    entries: ToolCallEntry[]
  ): TranscriptEntry[] => {
    return entries.reduce<TranscriptEntry[]>((acc, entry) => {
      if (entry.role === 'agent' || entry.role === 'user') {
        const utterance = entry as PhoneCallResponse.Utterance;
        acc.push({
          role: utterance.role,
          content: utterance.content || '',
        });
      }
      return acc;
    }, []);
  };

  const parseTranscript = (): TranscriptEntry[] => {
    if (!callDetails) return [];

    const callRecord = callDetails as unknown as Record<string, unknown>;

    const scrubbedTranscript = callRecord.scrubbed_transcript_with_tool_calls;
    if (Array.isArray(scrubbedTranscript) && scrubbedTranscript.length > 0) {
      return parseToolCallTranscript(scrubbedTranscript as ToolCallEntry[]);
    }

    if (
      callDetails.transcript_with_tool_calls &&
      Array.isArray(callDetails.transcript_with_tool_calls) &&
      callDetails.transcript_with_tool_calls.length > 0
    ) {
      return parseToolCallTranscript(
        callDetails.transcript_with_tool_calls as ToolCallEntry[]
      );
    }

    if (
      callDetails.transcript_object &&
      Array.isArray(callDetails.transcript_object)
    ) {
      return callDetails.transcript_object.map(
        (entry: PhoneCallResponse.TranscriptObject) => ({
          role: entry.role === 'agent' ? 'agent' : 'user',
          content: entry.content || '',
        })
      );
    }

    if (callDetails.transcript && typeof callDetails.transcript === 'string') {
      const lines = callDetails.transcript.split('\n');
      const entries: TranscriptEntry[] = [];

      lines.forEach((line) => {
        const trimmed = line.trim();
        if (trimmed.startsWith('Agent:')) {
          entries.push({
            role: 'agent',
            content: trimmed.replace('Agent:', '').trim(),
          });
        } else if (trimmed.startsWith('User:')) {
          entries.push({
            role: 'user',
            content: trimmed.replace('User:', '').trim(),
          });
        }
      });

      return entries;
    }

    return [];
  };

  const transcriptEntries = parseTranscript();

  if (transcriptEntries.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Transcription
        </Typography>
        <Typography color="text.secondary">
          No transcript available for this call.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Transcription
      </Typography>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {transcriptEntries.map((entry, index) => (
          <Box
            key={index}
            sx={{
              bgcolor: '#f5f5f5',
              p: 2,
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: entry.role === 'agent' ? '#2c3e7d' : '#555',
                }}
              >
                {entry.role === 'agent' ? 'Agent' : 'User'}
              </Typography>
              {entry.timestamp && (
                <Typography variant="caption" color="text.secondary">
                  {formatTimestamp(entry.timestamp)}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {entry.content}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default RetellCallTranscriptionSection;
